import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useRecordsApi } from "../../lib/api/records";
import type { DeltaStatic, Sources } from "quill";
import Quill from "quill";
import { Loader2, X, Link as LinkIcon, Book, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { LoadingOverlay } from "../../components/ui/LoadingOverlay";
import { WritingAssistant } from "../../components/admin/WritingAssistant";

interface Reference {
  reference_type_id: number;
  reference: string;
}

interface FormData {
  title: string;
  content: string;
  formatted_content: DeltaStatic;
  references: Reference[];
}

interface RecordData {
  id: string;
  title: string;
  content: string;
  formatted_content: DeltaStatic;
  references: Reference[];
  created_at: string;
  updated_at: string;
}

export function EditRecordPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const recordsApi = useRecordsApi();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [newReference, setNewReference] = useState("");
  const [selectedReferenceTypeId, setSelectedReferenceTypeId] = useState<
    number | null
  >(null);
  const quillRef = useRef<ReactQuill>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [showWritingAssistant, setShowWritingAssistant] = useState(false);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
        ["undo", "redo"],
      ],
      handlers: {
        undo: function (this: { quill: { history: { undo: () => void } } }) {
          this.quill.history.undo();
        },
        redo: function (this: { quill: { history: { redo: () => void } } }) {
          this.quill.history.redo();
        },
      },
    },
    history: {
      delay: 1000,
      maxStack: 100,
      userOnly: true,
    },
  }), []);

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const { data: record, isLoading } = useQuery({
    queryKey: ["record", id],
    queryFn: async () => {
      const response = await recordsApi.getRecord(id!);
      return response as unknown as RecordData;
    },
    enabled: !!id,
  });

  const { data: referenceTypes, isLoading: isLoadingReferenceTypes } = useQuery(
    {
      queryKey: ["reference-types"],
      queryFn: async () => {
        const types = await recordsApi.getReferenceTypes();
        return types.map((type) => ({ ...type, id: parseInt(type.id, 10) }));
      },
    }
  );

  useEffect(() => {
    if (record) {
      // Initialize with empty Quill contents if no formatted_content exists
      const emptyDelta = new Quill(document.createElement("div")).getContents();
      const initialContent = record.formatted_content || emptyDelta;

      setFormData({
        title: record.title,
        content: record.content || "",
        formatted_content: initialContent,
        references: record.references || [],
      });
    }
  }, [record]);

  const updateMutation = useMutation({
    mutationFn: (updates: FormData) => recordsApi.updateRecord(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["record", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-records"] });
      navigate("/admin/records");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setSaving(true);
      await updateMutation.mutateAsync(formData!);
    } catch (error) {
      console.error("Failed to update record:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev!,
      [name]: value,
    }));
  };

  const handleEditorChange = (
    content: string,
    delta: DeltaStatic,
    source: Sources,
    editor: ReactQuill.UnprivilegedEditor
  ) => {
    setFormData((prev) => ({
      ...prev!,
      content: editor.getText(), // Get plain text
      formatted_content: editor.getContents(), // Get Delta format
    }));
  };

  /**
   * Handle accepting enhanced text from WritingAssistant.
   * The text parameter now contains HTML content from the LLM.
   */
  const handleAcceptEnhanced = (htmlContent: string, formattedContent?: DeltaStatic) => {
    // Create a temporary Quill instance to convert HTML to Delta if needed
    if (formattedContent) {
      // Use Delta format directly if available
      // Extract plain text from Delta
      const tempContainer = document.createElement('div');
      const tempQuill = new Quill(tempContainer);
      tempQuill.setContents(formattedContent);
      const plainText = tempQuill.getText().trim();

      setFormData((prev) => ({
        ...prev!,
        content: plainText,
        formatted_content: formattedContent,
      }));
    } else if (htmlContent) {
      // Convert HTML to Delta format using a temporary Quill instance
      const tempContainer = document.createElement('div');
      const tempQuill = new Quill(tempContainer);
      tempQuill.clipboard.dangerouslyPasteHTML(0, htmlContent);
      const newDelta = tempQuill.getContents();
      const plainText = tempQuill.getText().trim();

      setFormData((prev) => ({
        ...prev!,
        content: plainText,
        formatted_content: newDelta,
      }));
    }
    setShowWritingAssistant(false);
  };

  /**
   * Handle using original text (close assistant without changes).
   */
  const handleUseOriginal = () => {
    setShowWritingAssistant(false);
  };

  const handleAddReference = (e?: React.KeyboardEvent<HTMLInputElement>) => {
    // Only proceed if it's a button click (no event) or Enter key press
    if (e && e.key !== "Enter") {
      return;
    }

    if (e?.key === "Enter") {
      e.preventDefault();
    }

    if (newReference.trim() && selectedReferenceTypeId !== null) {
      const value = newReference.trim();
      const selectedType = referenceTypes?.find(
        (type) => type.id === selectedReferenceTypeId
      );

      if (selectedType?.name === "Link" && !isValidUrl(value)) {
        console.log("Invalid URL detected");
        return; // Don't add invalid URLs
      }

      setFormData((prev) => ({
        ...prev!,
        references: [
          ...prev!.references,
          { reference_type_id: selectedReferenceTypeId, reference: value },
        ],
      }));
      setNewReference("");
    }
  };

  const handleRemoveReference = (index: number) => {
    setFormData((prev) => ({
      ...prev!,
      references: prev!.references.filter((_, i) => i !== index),
    }));
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getPreview = (reference: Reference) => {
    const refType = referenceTypes?.find(
      (type) => type.id === reference.reference_type_id
    );

    if (refType?.name === "Link") {
      try {
        const urlObj = new URL(reference.reference);
        return (
          urlObj.hostname +
          urlObj.pathname.slice(0, 20) +
          (urlObj.pathname.length > 20 ? "..." : "")
        );
      } catch {
        return reference.reference;
      }
    }
    return reference.reference;
  };

  const getTypeIcon = (typeId: number) => {
    const refType = referenceTypes?.find((type) => type.id === typeId);
    switch (refType?.name) {
      case "Link":
        return <LinkIcon className="h-3 w-3" />;
      case "Book":
        return <Book className="h-3 w-3" />;
      default:
        return null;
    }
  };

  if (isLoading || !formData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg">Record not found</div>
      </div>
    );
  }

  return (
    <>
      <LoadingOverlay
        isVisible={saving || updateMutation.isPending}
        message="Mise à jour du document"
        subMessage="Regénération des embeddings et du résumé. Cela peut prendre quelques instants..."
      />
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Edit Record
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700"
            >
              Question
            </label>
            <input
              type="text"
              name="title"
              id="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={saving || updateMutation.isPending}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="content"
                className="block text-sm font-medium text-gray-700"
              >
                Answer
              </label>
              <button
                type="button"
                onClick={() => setShowWritingAssistant(!showWritingAssistant)}
                disabled={saving || updateMutation.isPending}
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="h-4 w-4" />
                {showWritingAssistant ? "Hide AI Assistant" : "Enhance with AI"}
                {showWritingAssistant ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Writing Assistant Panel */}
            {showWritingAssistant && (
              <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
                <WritingAssistant
                  question={formData.title}
                  initialText={formData.content}
                  initialFormattedContent={formData.formatted_content}
                  onAcceptEnhanced={handleAcceptEnhanced}
                  onUseOriginal={handleUseOriginal}
                  disabled={saving || updateMutation.isPending}
                  originalLabel="Current Answer"
                  enhancedLabel="Enhanced Answer"
                />
              </div>
            )}

            {/* Standard Editor */}
            {!showWritingAssistant && (
              <div className="mt-1">
                <ReactQuill
                  ref={quillRef}
                  value={formData.formatted_content}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                  theme="snow"
                  readOnly={saving || updateMutation.isPending}
                  className="h-64 bg-white [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:border-gray-300 [&_.ql-toolbar]:border-gray-300 [&_.ql-container]:bg-white [&_.ql-toolbar]:bg-white"
                />
              </div>
            )}
          </div>

          <div className="pt-8">
            <label
              htmlFor="references"
              className="block text-sm font-medium text-gray-700"
            >
              References
            </label>
            <div className="mt-1 space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={selectedReferenceTypeId?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedReferenceTypeId(
                      value ? parseInt(value, 10) : null
                    );
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={
                    isLoadingReferenceTypes ||
                    saving ||
                    updateMutation.isPending
                  }
                >
                  <option value="">Select type...</option>
                  {referenceTypes?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={newReference}
                    onChange={(e) => setNewReference(e.target.value)}
                    onKeyDown={handleAddReference}
                    placeholder={
                      selectedReferenceTypeId !== null
                        ? `${
                            referenceTypes?.find(
                              (t) => t.id === selectedReferenceTypeId
                            )?.name === "Link"
                              ? "🔗"
                              : "📚"
                          } Enter ${
                            referenceTypes?.find(
                              (t) => t.id === selectedReferenceTypeId
                            )?.name
                          } and press Enter`
                        : "Select a reference type first"
                    }
                    disabled={
                      selectedReferenceTypeId === null ||
                      saving ||
                      updateMutation.isPending
                    }
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-12 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddReference()}
                    disabled={
                      selectedReferenceTypeId === null ||
                      !newReference.trim() ||
                      saving ||
                      updateMutation.isPending
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-indigo-600 p-1 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="block h-4 w-4 font-semibold leading-4">
                      +
                    </span>
                  </button>
                </div>
              </div>
              {formData.references.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.references.map((ref, index) => {
                    const refType = referenceTypes?.find(
                      (type) => type.id === ref.reference_type_id
                    );
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                          refType?.name === "Link"
                            ? "bg-indigo-50 text-indigo-700"
                            : refType?.name === "Book"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-blue-50 text-blue-700"
                        }`}
                      >
                        {getTypeIcon(ref.reference_type_id)}
                        <span>{getPreview(ref)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveReference(index)}
                          disabled={saving || updateMutation.isPending}
                          className="ml-1 rounded-full p-0.5 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-8">
            <button
              type="button"
              onClick={() => navigate("/admin/records")}
              disabled={saving || updateMutation.isPending}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || updateMutation.isPending}
              className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving || updateMutation.isPending
                ? "Saving..."
                : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
